import { CustomFormApi, Form } from 'informed';
import { useSearchParams } from 'next/navigation';
import { FC, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { AnalyticsEventName } from '@/api/analytics';
import {
  CouponCodeStatus,
  setCouponCode as requestPromocodeActivation,
} from '@/api/auth';
import { Button } from '@/components/buttons';
import { Input } from '@/components/form/Input';
import { ThemeMode } from '@/helpers/enums';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { required } from '@/helpers/validators';
import { useAnalytics } from '@/hooks/analytics';
import { useCommonStore, useModalsStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScHavePromocode, ScPromocodeFormWrapper } from './styled';
import { ScPromoCodeInput } from '@/components/Deposit/components/InputPromo/styled';

export const PROMOCODE_SECTION_QUERY = 'promocode';
const DISTANCE_TO_TOP = 100;

interface PromocodeApi {
  promocode: string;
}

export interface HavePromocodeSectionProps {
  onPromocodeActivationSuccess: () => void;
}

export const HavePromocodeSection: FC<HavePromocodeSectionProps> = ({
  onPromocodeActivationSuccess,
}) => {
  const formApiRef = useRef<CustomFormApi<PromocodeApi>>();
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('bonuses');
  const { sendEventToAnalytics } = useAnalytics();
  const searchParams = useSearchParams();

  const { blackColor } = useTheme();

  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const open = useModalsStore.use.open();

  const [promocodePending, setPromocodePending] = useState(false);

  const [isError, setIsError] = useState(false);
  const [inputPaddingRight, setInputPaddingRight] = useState(0);

  const handleChange = useCallback(() => {
    setIsError(false);
    formApiRef.current?.setError('promocode', undefined);
  }, []);

  const handleSubmit = async (values: PromocodeApi) => {
    if (!isAuthenticated) return open(Modals.Registration);

    try {
      setPromocodePending(true);

      const { data, error } = await requestPromocodeActivation({
        couponCode: values.promocode,
      });

      if (!data || data.status === CouponCodeStatus.FailedToActivate || error) {
        formApiRef.current?.setError('promocode', t('claimBonus.error'));
        setIsError(true);

        sendEventToAnalytics({
          name: AnalyticsEventName.PROMO__PROMOCODE_ACTIVATION__FAILURE,
          attributes: {
            additionalData: JSON.stringify({ promocode: values.promocode }),
          },
        });

        return;
      }

      if (data.status === CouponCodeStatus.SuccessfullyActivated) {
        showTooltip({
          type: TooltipTypes.SUCCESS,
          body: t('claimBonus.successTooltip.description'),
        });

        sendEventToAnalytics({
          name: AnalyticsEventName.PROMO__PROMOCODE_ACTIVATION__SUCCESS,
          attributes: {
            additionalData: JSON.stringify({ promocode: values.promocode }),
          },
        });

        formApiRef.current?.setValue('promocode', '');

        onPromocodeActivationSuccess();

        return;
      }
    } catch (e) {
      console.log(e);
    } finally {
      setPromocodePending(false);
    }
  };

  useLayoutEffect(() => {
    const block = searchParams.get('block');

    const ifBonusesRendered = () => {
      if (ref.current) {
        window.scrollTo({
          top:
            window.scrollY +
            ref.current?.getBoundingClientRect().top -
            DISTANCE_TO_TOP,
          behavior: 'smooth',
        });
      }
    };

    const timeoutId = setTimeout(() => {
      if (block === PROMOCODE_SECTION_QUERY) {
        ifBonusesRendered();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchParams]);

  return (
    <ScHavePromocode ref={ref}>
      <div className="text">
        <div className="title">{t('claimBonus.title')}</div>
        <div className="description">{t('claimBonus.description')}</div>
      </div>
      <Form<PromocodeApi>
        onSubmit={({ values }) => handleSubmit(values)}
        formApiRef={formApiRef}
      >
        <ScPromocodeFormWrapper>
          <div className="input-wrapper">
            <ScPromoCodeInput
              mode={ThemeMode.Dark}
              inputPaddingRight={inputPaddingRight}
            >
              <Input
                autoComplete="off"
                validateOn="blur"
                placeholder={t('claimBonus.enterCode')}
                name="promocode"
                validate={required}
                onChange={handleChange}
                mode={ThemeMode.Dark}
                background={blackColor}
                addons={
                  <div
                    ref={(node: HTMLDivElement | null) =>
                      setInputPaddingRight(node?.clientWidth || 0)
                    }
                  >
                    <Button
                      isLoading={promocodePending}
                      type="submit"
                      disabled={isError}
                      secondary
                      xSmall
                      autoPaddingHorizontal
                      paddingX={11}
                      className="promo-btn"
                    >
                      {t('activate')}
                    </Button>
                  </div>
                }
              />
            </ScPromoCodeInput>
          </div>
        </ScPromocodeFormWrapper>
      </Form>
    </ScHavePromocode>
  );
};
